import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { m } from 'framer-motion';
// next
import { useRouter } from 'next/router';
// @mui
import { Box, Typography, MenuItem, Button, Stack } from '@mui/material';
// hooks
import useAuth from '../../../hooks/useAuth';
// components
import CompanyAvatar from '../../../sections/company/CompanyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import Iconify from '../../../components/Iconify';
import SvgIconStyle from '../../../components/SvgIconStyle';
import {
  TimeOnDialog,
  TimeOffConfirmDialog,
  FirstOnDialog,
  GoalAddDialog,
} from '../../../sections/timetracking';
import useGoals from '../../../hooks/useGoals';
// swr
import useSWR from 'swr';
import { mutate as swrMutate } from "swr";
// utils
import axios from '../../../utils/axios';
import axiosSWRFetcher from '../../../utils/swrFetcher';
import { fKrTrackingTimeShortKST } from '../../../utils/formatTime';
// routes
import { PATH_TIME } from '../../../routes/paths';

// ----------------------------------------------------------------------

export default function AttendancePopover() {
  const { user } = useAuth();
  const { data, mutate } = useSWR(user ? `/api/works/time-tracking/crew/status/` : null, axiosSWRFetcher, {errorRetryInterval: 2000, revalidateOnFocus: false});

  const router = useRouter();
  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);
  const [isOnDialogOpen, setOnDialogOpen] = useState(false);
  const [isOffConfirmDialogOpen, setIsOffConfirmDialogOpen] = useState(false);
  const [isFirstOnDialogOpen, setIsFirstOnDialogOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [isGoalAddDialogOpen, setIsGoalAddDialogOpen] = useState(false);
  const [pendingClockIn, setPendingClockIn] = useState(false);

  // 선택된 회사의 코어타임 정보 가져오기
  const { data: coreTimeData } = useSWR(
    selectedCompanyId && isOffConfirmDialogOpen ? `/api/works/time-tracking/crew/company/${selectedCompanyId}/` : null, 
    axiosSWRFetcher, 
    {errorRetryInterval: 2000, revalidateOnFocus: false}
  );

  // 선택된 회사의 목표 정보 가져오기
  const { totalGoals } = useGoals(selectedCompanyId);

  const pastTime = data?.status?.current_check_in?.start_at ? new Date().getTime() - new Date(data?.status?.current_check_in?.start_at).getTime() : 0;
  const hours = Math.max(Math.floor(pastTime / (1000 * 60 * 60)), 0);
  const minutes = Math.max(Math.floor((pastTime % (1000 * 60 * 60)) / (1000 * 60)), 0);

  const isClockedIn = data?.status?.current_check_in?.start_at ? true : false;

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
    setSelectedCompanyId(null);
  };

  const trackingOn = async (data) => {
    try {
      const response =await axios.post(`/api/works/time-tracking/crew/company/${selectedCompanyId}/on/`, data);
      enqueueSnackbar('근무가 시작되었습니다.', { variant: 'success' });
      setOnDialogOpen(false);
      mutate();
      if (response.data.first_on) {
        setIsFirstOnDialogOpen(true);
      } else {
        router.push({pathname: PATH_TIME.root, query: {c: selectedCompanyId}});
      }
      handleClose();
    } catch (err) {
      if (err.response && err.response.status === 400) {
        enqueueSnackbar(err.response.data.message, { variant: 'warning' });
      }
      mutate();
      console.error(err);
    }
  };

  const trackingOff = async (offData) => {
    try {
      const timeTrackingItemId = offData.time_tracking_item_id;
      const postData = {
        end_at: offData.end_at,
        modified_start_at: offData.modified_start_at,
        modified_end_at: offData.modified_end_at,
        dones: [...offData.dones],
        undones: [...offData.undones],
        removes: [...offData.removes],
        next_work_schedule: offData.next_work_schedule,
      }
      await axios.post(`/api/works/time-tracking/crew/company/${selectedCompanyId}/${timeTrackingItemId}/off/`, postData);
      enqueueSnackbar('근무가 종료되었습니다.', { variant: 'success' });
      swrMutate(`/api/works/time-tracking/crew/company/${selectedCompanyId}/?`);
    } catch (err) {
      if (err.response && err.response.status === 400) {
        enqueueSnackbar(err.response.data.message, { variant: 'warning' });
      }
      console.error(err);
    }
  };

  const closeFirstOnDialog = () => {
    setIsFirstOnDialogOpen(false);
    mutate();
    handleClose();
    router.push({pathname: PATH_TIME.root, query: {c: selectedCompanyId}});
  };

  const openTrackingOffConfirm = async () => {
    setSelectedCompanyId(data?.status?.current_check_in?.company_id);
    setIsOffConfirmDialogOpen(true);
  };

  const closeTrackingOffConfirm = () => {
    setIsOffConfirmDialogOpen(false);
    setSelectedCompanyId(null);
    handleClose();
    mutate();
  };

  const handleClockIn = async (companyId) => {
    setSelectedCompanyId(companyId);
    setPendingClockIn(true);
  };

  // 목표 체크 후 처리
  useEffect(() => {
    if (pendingClockIn && selectedCompanyId && totalGoals !== undefined) {
      if (totalGoals === 0) {
        // 목표가 없으면 목표 추가 다이얼로그 열기
        setIsGoalAddDialogOpen(true);
        setPendingClockIn(false);
      } else {
        // 목표가 있으면 출근 다이얼로그 열기
        setOnDialogOpen(true);
        setPendingClockIn(false);
      }
    }
  }, [pendingClockIn, selectedCompanyId, totalGoals]);

  const handleGoalAddSuccess = () => {
    setIsGoalAddDialogOpen(false);
    // 목표 추가 성공 후 출근 다이얼로그 열기
    setOnDialogOpen(true);
  };

  const handleGoalAddCancel = () => {
    setIsGoalAddDialogOpen(false);
    setPendingClockIn(false);
    handleClose(); // MenuPopover 닫기
  };

  return (
    <>
      <Box
        component={m.div}
        whileTap="tap"
        whileHover="hover"
        variants={{hover: { scale: 1.03 }, tap: { scale: 0.97 }}}
        sx={{
          display: 'inline-flex',
        }}
      >
        <Button
          startIcon={
            isClockedIn ? (
              <SvgIconStyle src={'/icons/ic_door.svg'} sx={{ width: 16, height: 16 }} />
            ) : (
              <SvgIconStyle src={'/icons/icon-play-mono.svg'} sx={{ width: 16, height: 16 }} />
            )
          }
          variant="outlined"
          onClick={handleOpen}
          size="medium"
          sx={{
            borderRadius: 3,
            borderColor: "divider",
            color: "text.secondary",
            '&:hover': {
              bgcolor: "action.hover",
              borderColor: "divider",
            },
            '&:active': {
              bgcolor: "action.selected",
              borderColor: "divider",
              color: "text.primary",
            },
          }}
          aria-label={isClockedIn ? '퇴근하기' : '출근하기'}
          tabIndex={0}
        >
          {isClockedIn ? '퇴근하기' : '출근하기'}
        </Button>
      </Box>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        disabledArrow
        sx={{
          p: 2.5,
          mt: 1,
          width: 300,
        }}
      >
        {(!isClockedIn && data?.status?.available_companies && data.status.available_companies.length > 1) ? (
          <>
            <Typography variant="subtitle1" noWrap paragraph>
              어떤 회사의 업무를 시작하실건가요?
            </Typography>
            <Stack spacing={1}>
            {data.status.available_companies.map((company) => (
              <MenuItem
                key={company.company_id}
                onClick={() => handleClockIn(company.company_id)}
                sx={{ 
                  p: 2,
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1.5,
                }}
                aria-label={`${company.company_name} 출근하기`}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleClockIn(company.company_id);
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CompanyAvatar companyId={company.company_id} />
                </Box>
                <Iconify icon="eva:arrow-ios-forward-fill" width={20} height={20} sx={{ color: 'text.secondary' }} />
              </MenuItem>
            ))}
            </Stack>
          </>
        ) : (!isClockedIn && data?.status?.available_companies && data.status.available_companies.length == 1) ? (
          <>
            <Typography variant="subtitle1" paragraph sx={{ wordBreak: "break-all" }}>
              {data?.status?.available_companies?.[0]?.company_name} 회사의<br/> 업무를 시작하실건가요?
            </Typography>
            <Button
              variant="contained"
              onClick={() => handleClockIn(data?.status?.available_companies?.[0]?.company_id)}
              fullWidth
              size="large"
              sx={{ mt: 1 }}
              aria-label={`${data?.status?.available_companies?.[0]?.company_name} 출근하기`}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  handleClockIn(data?.status?.available_companies?.[0]?.company_id);
                }
              }}
            >
              출근하기
            </Button>
          </>
        ) : isClockedIn ? (
          <>
            <Typography variant="subtitle1" paragraph sx={{ wordBreak: "break-all" }}>
              {data?.status?.current_check_in?.company_name} 회사의<br/> 업무를 마무리하실 건가요?
            </Typography>
            <Box
              sx={{
                p: 2,
                my: 2,
                backgroundColor: "background.dashboard",
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: 18, height: 18, color: "secondary.main" }} />
                <Typography variant={"subtitle2"} sx={{ color: "secondary.main" }}>
                  {hours > 0 ? `${hours}시간` : ""} {minutes}분 근무중
                </Typography>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width: 18, height: 18, visibility: "hidden" }} />
                <Typography variant={"body2"} sx={{ color: "text.secondary" }}>
                  {fKrTrackingTimeShortKST(data?.status?.current_check_in?.start_at)} ~
                </Typography>
              </Stack>
            </Box>
            <Button
              variant="contained"
              onClick={openTrackingOffConfirm}
              fullWidth
              size="large"
              sx={{ mt: 1 }}
              aria-label={`${data?.status?.current_check_in?.company_name} 퇴근하기`}
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  openTrackingOffConfirm();
                }
              }}
            >
              퇴근하기
            </Button>
          </>
        ) : null}
      </MenuPopover>
      <TimeOnDialog
        onCompanyId={selectedCompanyId}
        isOpen={isOnDialogOpen}
        closeDialog={() => setOnDialogOpen(false)}
        trackingOn={trackingOn}
      />
      <TimeOffConfirmDialog
        onCompanyId={selectedCompanyId}
        timeTrackingData={data?.status?.current_check_in}
        isOpen={isOffConfirmDialogOpen}
        closeDialog={closeTrackingOffConfirm}
        trackingOff={trackingOff}
        coreTime={coreTimeData?.core_time}
      />
      <FirstOnDialog
        isOpen={isFirstOnDialogOpen}
        closeDialog={closeFirstOnDialog}
      />
      <GoalAddDialog
        isOpen={isGoalAddDialogOpen}
        closeDialog={handleGoalAddCancel}
        companyId={selectedCompanyId}
        onSuccess={handleGoalAddSuccess}
        fromTimeOn
      />
    </>
  );
}
